import React, { useState, useEffect } from 'react';
import { X, Copy, Loader2 } from 'lucide-react';
import { RyanTab } from '../types/ryan.types';
import './RyanMoveModal.css';

interface SpaceOption {
    _id: string;
    name: string;
    tabs: RyanTab[];
}

interface RyanCopyModalProps {
    spaceId: string;
    subdeckId: string;
    subdeckName: string;
    currentTabs: RyanTab[];
    onClose: () => void;
    onSuccess: () => void;
    onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const RyanCopyModal: React.FC<RyanCopyModalProps> = ({
    spaceId, subdeckId, subdeckName, currentTabs, onClose, onSuccess, onToast
}) => {
    const token = () => localStorage.getItem('accessToken') || '';

    const [destType, setDestType] = useState<'same' | 'other'>('same');
    const [spaces, setSpaces] = useState<SpaceOption[]>([]);
    const [selectedSpaceId, setSelectedSpaceId] = useState('');
    const [selectedTabId, setSelectedTabId] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await fetch('/api/ryan/admin/spaces', {
                    headers: { Authorization: `Bearer ${token()}` }
                });
                if (r.ok) {
                    const data = await r.json();
                    // Include all spaces (including current) for copy
                    setSpaces(data);
                }
            } catch { /* ignore */ }
            finally { setLoading(false); }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedCurrentTabs = [...currentTabs].sort((a, b) => a.order - b.order);

    const otherSpaceTabs: RyanTab[] = destType === 'other' && selectedSpaceId
        ? ([...(spaces.find(s => s._id === selectedSpaceId)?.tabs || [])]).sort((a, b) => a.order - b.order)
        : [];

    const tabsToShow = destType === 'same' ? sortedCurrentTabs : otherSpaceTabs;

    const showTabSelector = destType === 'same' || (destType === 'other' && !!selectedSpaceId);
    const canConfirm = destType === 'same' || !!selectedSpaceId;

    const handleCopy = async () => {
        if (!canConfirm || submitting) return;
        setSubmitting(true);
        const targetSpaceId = destType === 'same' ? spaceId : selectedSpaceId;
        try {
            const r = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeckId}/copy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ targetSpaceId, targetTabId: selectedTabId || null })
            });
            if (r.ok) {
                onToast('Deck copiado com sucesso!', 'success');
                onSuccess();
                onClose();
            } else {
                const e = await r.json();
                onToast(e.message || 'Erro ao copiar', 'error');
            }
        } catch {
            onToast('Erro ao copiar', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rmm-overlay" onClick={onClose}>
            <div className="rmm-dialog" onClick={e => e.stopPropagation()}>
                <button className="rmm-close" onClick={onClose}><X size={18} /></button>

                <h2 className="rmm-title">Copiar Deck</h2>

                <div className="rmm-source">
                    <span className="rmm-source-label">Copiando:</span>
                    <span className="rmm-source-name">{subdeckName}</span>
                </div>

                {loading ? (
                    <div className="rmm-loading">
                        <Loader2 size={20} className="rmm-spin" /> Carregando campos...
                    </div>
                ) : (
                    <>
                        <div className="rmm-field">
                            <label className="rmm-label">Destino</label>
                            <div className="rmm-radio-group">
                                <label className="rmm-radio-label">
                                    <input
                                        type="radio"
                                        name="destType"
                                        checked={destType === 'same'}
                                        onChange={() => { setDestType('same'); setSelectedSpaceId(''); setSelectedTabId(''); }}
                                    />
                                    Este campo de estudo
                                </label>
                                <label className="rmm-radio-label">
                                    <input
                                        type="radio"
                                        name="destType"
                                        checked={destType === 'other'}
                                        onChange={() => { setDestType('other'); setSelectedTabId(''); }}
                                    />
                                    Outro campo de estudo
                                </label>
                            </div>
                        </div>

                        {destType === 'other' && (
                            <div className="rmm-field">
                                <label className="rmm-label">Campo destino</label>
                                {spaces.length === 0 ? (
                                    <p className="rmm-info">Nenhum campo disponível.</p>
                                ) : (
                                    <select
                                        className="rmm-select"
                                        value={selectedSpaceId}
                                        onChange={e => { setSelectedSpaceId(e.target.value); setSelectedTabId(''); }}
                                    >
                                        <option value="">Selecione...</option>
                                        {spaces.map(s => (
                                            <option key={s._id} value={s._id}>{s.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        {showTabSelector && (
                            <div className="rmm-field">
                                <label className="rmm-label">Aba destino</label>
                                <select
                                    className="rmm-select"
                                    value={selectedTabId}
                                    onChange={e => setSelectedTabId(e.target.value)}
                                >
                                    <option value="">Sem aba</option>
                                    {tabsToShow.map(t => (
                                        <option key={t._id} value={t._id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="rmm-actions">
                            <button className="rmm-btn-cancel" onClick={onClose}>Cancelar</button>
                            <button
                                className="rmm-btn-confirm"
                                onClick={handleCopy}
                                disabled={!canConfirm || submitting}
                            >
                                {submitting
                                    ? <><Loader2 size={15} className="rmm-spin" /> Copiando...</>
                                    : <><Copy size={15} /> Copiar</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RyanCopyModal;
